/** When a chat message was sent, parsed from history and formatted for the hover row. */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Matches a trailing `Z` or `±hh:mm` / `±hhmm` offset on an ISO string. */
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse a stored message time into epoch ms, or undefined when absent or
 * unparseable. The server sends ISO strings converted to the user's timezone,
 * offset included; one with no offset is UTC, as stored. Reading it as local
 * time instead would shift every time by the browser's UTC offset.
 */
export function parseMessageTime(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const ms = Date.parse(HAS_OFFSET.test(raw) ? raw : `${raw}Z`);
  return Number.isNaN(ms) ? undefined : ms;
}

/** Spreadable `sentAt` (absent rather than undefined, for exactOptionalPropertyTypes). */
export function sentAtFrom(raw: string | undefined): { sentAt?: number } {
  const sentAt = parseMessageTime(raw);
  return sentAt === undefined ? {} : { sentAt };
}

/** Midnight at the start of `ms`'s local day. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export interface MessageTimeLabel {
  /** "9:14 AM" today, "Yesterday 4:52 PM", "Oct 26, 4:52 PM", or with the year when not this year. */
  short: string;
  /** "Tuesday, October 27, 2026 at 4:52 PM": the hover title. */
  full: string;
}

/**
 * The label shown beside a message's actions. Short enough to sit in the hover
 * row; the full date is its title. `now` and `locale` are parameters so the
 * output is testable.
 */
export function formatMessageTime(
  sentAt: number,
  now: number = Date.now(),
  locale?: string
): MessageTimeLabel {
  const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(sentAt);
  const full = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(sentAt);

  const today = startOfDay(now);
  const day = startOfDay(sentAt);
  if (day === today) return { short: time, full };
  // Rounded: a DST change makes one day 23 or 25 hours long.
  if (Math.round((today - day) / DAY_MS) === 1) return { short: `Yesterday ${time}`, full };

  const sameYear = new Date(sentAt).getFullYear() === new Date(now).getFullYear();
  const date = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(sentAt);
  return { short: `${date}, ${time}`, full };
}
